import "./FAQs.scss";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Accordion from "../../common/Accordion";
import PaymentModal from "../../common/PaymentModal";
import { type PaymentMethod } from "../../common/paymentConfig";
import { getFAQItems } from "./faqsData";

function FAQs() {
  const { hash } = useLocation();
  const [openedIds, setOpenedIds] = useState<string[]>([]);
  const [paymentModal, setPaymentModal] = useState<PaymentMethod | null>(null);
  const faqItems = useMemo(() => getFAQItems(setPaymentModal), []);

  function toggleQuestion(id: string) {
    setOpenedIds((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  }

  useEffect(() => {
    if (!hash) {
      return;
    }

    const targetId = hash.replace("#", "");
    const targetExists = faqItems.some((item) => item.id === targetId);

    if (!targetExists) {
      return;
    }

    setOpenedIds((prev) =>
      prev.includes(targetId) ? prev : [...prev, targetId],
    );

    // Wait for accordion state to apply, then scroll to the target question.
    window.requestAnimationFrame(() => {
      const targetEl = document.getElementById(targetId);
      if (!targetEl) {
        return;
      }

      const stickyHeaderOffset = 180;
      const y =
        targetEl.getBoundingClientRect().top +
        window.scrollY -
        stickyHeaderOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
  }, [hash, faqItems]);

  return (
    <div className="wrapper">
      <h1>Frequently asked questions</h1>
      <p>
        See below for frequently asked questions about your custom dessert
        order. Have something that's not covered below?{" "}
        <a href="mailto:connie@conniescakerie.com">Email me directly</a> or get
        in touch by <Link to="/order">submitting an order inquiry</Link>.
      </p>

      <div className="margin-top-md margin-bottom-lg">
        {faqItems.map((item) => {
          const isOpen = openedIds.includes(item.id);

          return (
            <Accordion
              key={item.id}
              id={item.id}
              className="faq-accordion"
              title={item.question}
              isOpen={isOpen}
              onToggle={() => toggleQuestion(item.id)}
              contentId={`${item.id}-content`}
            >
              {item.answer}
            </Accordion>
          );
        })}
      </div>

      <PaymentModal
        method={paymentModal}
        onClose={() => setPaymentModal(null)}
      />
    </div>
  );
}

export default FAQs;
